import { Box, Card, Divider, Grid, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Controller, FormProvider, useForm } from "react-hook-form";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import MDDatePicker from "components/MDDatePicker";
import { GenerateReportMutationVariables, ReportType } from "generated/graphql";
import { capitalCase } from "change-case";
import { useEffect, useRef, useState } from "react";
import { kebabCase } from "lodash";
import useGenerateReport from "hooks/organization/reports/useGenerateReport";
import DataTable from "components/Tables/DataTable/DataTable";
import useReportTableSalesByProductSummary from "hooks/organization/reports/types/useReportTableSalesByProductSummary";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import ProductOptions from "modules/products/ProductOptions";
import { useGetProduct } from "hooks/products/useGetProduct";
import { zodResolver } from "@hookform/resolvers/zod";
import { createProposalSchema } from "DDD/action-objects/ProposalCreate";
import { z } from "zod";
import dayjs from "dayjs";

// Extended schema for product filter
const reportFilterSchema = createProposalSchema.extend({
  productId: z.string().optional(),
});

type ReportFilterFormData = z.infer<typeof reportFilterSchema>;

const getReportDefaultValues = (
  initialValues: Partial<ReportFilterFormData>
): Partial<ReportFilterFormData> => ({
  companyId: "",
  productId: "",
  source: undefined,
  ...initialValues,
});

const ReportSalesByProductSummary = () => {
  const {
    handleSubmit,
    formState: { errors },
  } = useForm();

  let today = dayjs().format("YYYY-MM-DD 00:00:00");

  // Set start date to 1 month ago
  const startDateFormatted = dayjs().subtract(1, "month").format("YYYY-MM-DD 00:00:00");

  const [date, setDate] = useState({ startDate: startDateFormatted, endDate: today });
  const [generateReport, { data: generateReportData }] = useGenerateReport();
  const [productId, setProductId] = useState("");
  const [productName, setProductName] = useState("");

  const methods = useForm<ReportFilterFormData>({
    resolver: zodResolver(reportFilterSchema),
    defaultValues: getReportDefaultValues({}),
  });

  // Use table hook without toast for regular operations (pagination/filtering)
  const {
    tableData,
    pagination,
    loading,
    getReportSalesByProductSummary,
    setProductId: setTableProductId,
  } = useReportTableSalesByProductSummary(date.startDate, date.endDate);

  const [requestData, setRequestData] = useState<GenerateReportMutationVariables | null>(null);

  const downloadFileRef = useRef(null);

  useEffect(() => {
    if (generateReportData?.generateReport) {
      downloadFileRef.current.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${encodeURI(
        generateReportData.generateReport
      )}`;
      downloadFileRef.current.download = `bitumio-${kebabCase(requestData.type)}-report.xlsx`;
      downloadFileRef.current.click();
    }
  }, [generateReportData]);

  const changeDate = async (selectedDate: Array<Date>, type) => {
    const formattedDate = dayjs(selectedDate[0]).format("YYYY-MM-DD 00:00:00");
    setDate((date) => ({ ...date, [type]: formattedDate }));
  };

  const onSubmit = (values) => {
    // Trigger backend pagination search with current filters
    getReportSalesByProductSummary({
      variables: {
        ...date,
        productId: productId || undefined,
        page: 1,
        first: 25,
      },
    });
  };

  const handleDownload = (e) => {
    const variables = { type: ReportType.SALES_BY_PRODUCT_SUMMARY, ...date, productId };
    setRequestData(variables);
    generateReport({ variables });
  };

  // Format dates for display
  const formatDisplayDate = (dateString: string) => {
    return dayjs(dateString).format("MMM D, YYYY");
  };

  // Sync product name with selected product
  const { data: product } = useGetProduct({ id: productId || "" });

  useEffect(() => {
    if (productId === null || productId === undefined || productId === "") {
      setProductName("");
    } else if (product?.name) {
      setProductName(product.name);
    }
  }, [product, productId]);

  return (
    <Box
      sx={{
        "& .css-1h9h5al-MuiAutocomplete-root .MuiOutlinedInput-root": {
          padding: "5px !important",
        },
      }}
    >
      <DashboardPage>
        <FormProvider {...methods}>
          <MDBox mb={6} component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3} alignItems="center" display="flex" justifyContent="center">
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Card>
                  <MDBox
                    p={3}
                    lineHeight={1}
                    display="flex"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                  >
                    <Box>
                      <MDTypography variant="h4" fontWeight="medium">
                        {capitalCase(ReportType.SALES_BY_PRODUCT_SUMMARY)} Report
                      </MDTypography>
                      <MDTypography variant="body2" color="text" mt={1}>
                        View aggregated sales data by product type
                      </MDTypography>
                    </Box>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "info.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon sx={{ color: "#fff" }}>pie_chart</Icon>
                    </Box>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <MDBox p={3}>
                    <MDTypography variant="h6" mb={2}>
                      Filter Options
                    </MDTypography>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} md={3} lg={3}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Start Date
                          </MDTypography>
                          <MDDatePicker
                            defaultValue={startDateFormatted}
                            onChange={(val) => changeDate(val, "startDate")}
                            fullWidth
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={3} lg={3}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            End Date
                          </MDTypography>
                          <MDDatePicker
                            defaultValue={today}
                            onChange={(val) => changeDate(val, "endDate")}
                            fullWidth
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={3.5} lg={3.5}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Product
                          </MDTypography>
                          <ProductOptions>
                            {({ productOptions, loading, search }) => (
                              <Controller
                                name="productId"
                                control={methods.control}
                                render={({ field, fieldState: { error } }) => (
                                  <>
                                    <ScrollAsyncAutocomplete
                                      {...field}
                                      value={
                                        productOptions?.find((x) => x.value === field.value) || null
                                      }
                                      options={productOptions ?? []}
                                      loading={loading}
                                      label="Product"
                                      search={search}
                                      onLoadMore={() => {}}
                                      hasMore={false}
                                      onChange={(value) => {
                                        field.onChange(value || null);
                                        const newProductId = value ? String(value) : "";
                                        setProductId(newProductId);
                                        // Clear product name when filter is cleared
                                        if (!newProductId) {
                                          setProductName("");
                                        }
                                      }}
                                    />
                                  </>
                                )}
                              />
                            )}
                          </ProductOptions>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={1.5} lg={1.5}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <a ref={downloadFileRef} className="d-none" />
                          <MDButton color="success" type="submit" fullWidth sx={{ height: "40px" }}>
                            RUN REPORT
                          </MDButton>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={1} lg={1}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDButton
                            color="info"
                            onClick={handleDownload}
                            fullWidth
                            sx={{ height: "40px" }}
                          >
                            <Icon fontSize="small" sx={{ mr: 0.5 }}>
                              download
                            </Icon>
                          </MDButton>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, mb: 2 }}>
              <Grid container alignItems="center">
                <Grid item xs>
                  <MDTypography variant="h6" color="text">
                    Report Results
                  </MDTypography>
                </Grid>
                <Grid item>
                  <MDTypography variant="body2" color="text">
                    {productName ? `Product: ${productName}, ` : ""}
                    {date.startDate && date.endDate ? (
                      <>
                        Period: {formatDisplayDate(date.startDate)} -{" "}
                        {formatDisplayDate(date.endDate)}
                      </>
                    ) : (
                      "Select date range"
                    )}
                  </MDTypography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 1.5 }} />
            </Box>

            <Grid container display="flex">
              <DataTable
                canSearch={false}
                table={tableData}
                showTotalEntries={true}
                isSorted={true}
                pagination={pagination}
                paginationProps={{ variant: "gradient", color: "info" }}
                entriesPerPageOptions={["25", "50"]}
              />
              {tableData?.modal}
            </Grid>
          </MDBox>
        </FormProvider>
      </DashboardPage>
    </Box>
  );
};

export default ReportSalesByProductSummary;
